"use client";

interface BlogCardImageProps {
  category: string;
  title: string;
}

export default function BlogCardImage({ category, title }: BlogCardImageProps) {
  // Determine which illustration to show based on category/title
  const titleLower = title.toLowerCase();
  const isCLI = titleLower.includes("cli");
  const isExtension = titleLower.includes("extension") || titleLower.includes("vscode") || titleLower.includes("vs code");
  const isProduct = category === "Product";
  const isDocker = titleLower.includes("docker") || titleLower.includes("container") || titleLower.includes("dockerfile");
  const isOpenRouter = titleLower.includes("openrouter") || titleLower.includes("one api key");
  const isMCP = titleLower.includes("mcp") || titleLower.includes("model context protocol");
  const isGitHubPR = titleLower.includes("pull request") || titleLower.includes("pr comment") || titleLower.includes("inline") && titleLower.includes("github");
  const isDataflow = titleLower.includes("dataflow") || titleLower.includes("taint") || titleLower.includes("cross-file");
  const isGrpcBypass = titleLower.includes("grpc") || titleLower.includes("authorization bypass") || titleLower.includes(":path header");
  const isLangflowTraversal = titleLower.includes("langflow") || (titleLower.includes("path traversal") && titleLower.includes("bug they already fixed"));

  // Langflow KB Path Traversal visualization
  if (isLangflowTraversal) {
    return (
      <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-red-950/20 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden relative border border-gray-700/50">
        <style>{`
          @keyframes traversalPulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
        `}</style>

        {/* Terminal header */}
        <div className="absolute top-0 left-0 right-0 bg-gray-800/60 border-b border-gray-700/50 px-3 py-1.5 flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-400/60"></div>
          <div className="w-2 h-2 rounded-full bg-yellow-400/60"></div>
          <div className="w-2 h-2 rounded-full bg-green-400/60"></div>
          <span className="ml-2 text-gray-500 text-[8px] font-mono">variant analysis</span>
        </div>

        <div className="w-full h-full pt-8 px-4 pb-3 font-mono text-[9px] leading-[18px] space-y-1">
          {/* Scan command */}
          <div>
            <span className="text-green-400">$</span>
            <span className="text-gray-300"> pathfinder scan</span>
            <span className="text-blue-400"> --ruleset</span>
            <span className="text-yellow-400"> python/security</span>
            <span className="text-gray-300"> ./langflow</span>
          </div>

          <div className="h-px bg-gray-700/40 my-1"></div>

          {/* Finding */}
          <div className="flex items-center gap-2">
            <span className="text-cyan-400 font-semibold text-[8px]">SEC-115</span>
            <span className="text-[7px] px-1.5 py-0.5 rounded bg-red-400/20 text-red-400 border border-red-400/30 font-semibold">HIGH</span>
          </div>
          <div className="text-gray-500 text-[8px]">
            kb_helpers.py:<span className="text-yellow-400">212</span>
          </div>
          <div className="text-gray-400 text-[8px]">Path Traversal via Unsanitized Path Construction</div>

          <div className="h-px bg-gray-700/40 my-1"></div>

          {/* Exploit */}
          <div className="text-gray-600 text-[7px] uppercase tracking-wider">exploit</div>
          <div>
            <span className="text-green-400">$</span>
            <span className="text-gray-300"> curl</span>
            <span className="text-blue-400"> -X DELETE</span>
            <span className="text-yellow-400"> /api/v1/knowledge_bases/</span>
          </div>
          <div className="text-red-400 text-[8px]" style={{ animation: 'traversalPulse 2s ease-in-out infinite' }}>
            {"  "}kb_names: [&quot;../../../../../../tmp/victim&quot;]
          </div>

          {/* Response */}
          <div className="text-green-400/80 text-[8px]">
            {"{\"deleted_count\": 1}"}
          </div>

          {/* Bottom summary */}
          <div className="absolute bottom-2.5 left-3 right-3 flex items-center justify-between">
            <span className="text-red-400 text-[8px] font-semibold">shutil.rmtree on attacker path</span>
            <span className="text-gray-600 text-[8px]">langflow v1.8.4</span>
          </div>
        </div>
      </div>
    );
  }

  // gRPC-Go Authorization Bypass visualization
  if (isGrpcBypass) {
    return (
      <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-red-950/30 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden relative border border-gray-700/50">
        <style>{`
          @keyframes bypassPulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
        `}</style>

        {/* Terminal header */}
        <div className="absolute top-0 left-0 right-0 bg-gray-800/60 border-b border-gray-700/50 px-3 py-1.5 flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-400/60"></div>
          <div className="w-2 h-2 rounded-full bg-yellow-400/60"></div>
          <div className="w-2 h-2 rounded-full bg-green-400/60"></div>
          <span className="ml-2 text-gray-500 text-[8px] font-mono">HTTP/2 :path bypass</span>
        </div>

        <div className="w-full h-full pt-8 px-4 pb-3 font-mono text-[9px] leading-[18px] space-y-1">
          {/* Normal request */}
          <div className="text-gray-500 text-[8px]"># Normal request</div>
          <div>
            <span className="text-green-400">$</span>
            <span className="text-gray-300"> :path</span>
            <span className="text-blue-400"> /echo.EchoService/EchoBlocked</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">grpc-status:</span>
            <span className="text-red-400 font-semibold">7 (PermissionDenied)</span>
            <span className="text-[7px] px-1.5 py-0.5 rounded bg-green-400/15 text-green-400 font-semibold">BLOCKED</span>
          </div>

          <div className="h-px bg-gray-700/40 my-1"></div>

          {/* Malformed request */}
          <div className="text-gray-500 text-[8px]"># Malformed :path (no leading /)</div>
          <div>
            <span className="text-green-400">$</span>
            <span className="text-gray-300"> :path</span>
            <span className="text-yellow-400"> echo.EchoService/EchoBlocked</span>
          </div>
          <div className="flex items-center gap-2" style={{ animation: 'bypassPulse 2s ease-in-out infinite' }}>
            <span className="text-gray-500">grpc-status:</span>
            <span className="text-green-400 font-semibold">0 (OK)</span>
            <span className="text-[7px] px-1.5 py-0.5 rounded bg-red-400/20 text-red-400 border border-red-400/30 font-bold">BYPASS</span>
          </div>

          <div className="h-px bg-gray-700/40 my-1"></div>

          {/* Wire frame */}
          <div className="text-gray-500 text-[8px]">HPACK decoded:</div>
          <div className="pl-2 space-y-0.5">
            <div><span className="text-blue-400">:method</span> <span className="text-gray-400">POST</span></div>
            <div><span className="text-blue-400">:path</span>   <span className="text-red-400 font-semibold">echo.EchoService/EchoBlocked</span></div>
          </div>

          {/* Bottom summary */}
          <div className="absolute bottom-2.5 left-3 right-3 flex items-center justify-between">
            <span className="text-red-400 text-[8px] font-semibold">authz bypass confirmed</span>
            <span className="text-gray-600 text-[8px]">grpc-go v1.79.2</span>
          </div>
        </div>
      </div>
    );
  }

  // Cross-file dataflow analysis visualization (GitHub-style)
  if (isDataflow) {
    return (
      <div className="bg-[#0d1117] rounded-lg aspect-video mb-4 overflow-hidden relative border border-[#30363d]">
        <style>{`
          @keyframes ghPulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          @keyframes ghSlide {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 1; }
          }
        `}</style>

        {/* GitHub-style header bar */}
        <div className="bg-[#161b22] border-b border-[#30363d] px-3 py-1.5 flex items-center">
          <span className="text-[#8b949e] text-[9px] font-mono">src/routes/</span>
          <span className="text-[#58a6ff] text-[9px] font-mono">app.py</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[7px] px-1.5 py-0.5 rounded-full bg-[#f0883e]/15 text-[#f0883e] font-mono font-semibold">SOURCE</span>
            <span className="text-[#8b949e] text-[7px] font-mono">→</span>
            <span className="text-[7px] px-1.5 py-0.5 rounded-full bg-[#f85149]/15 text-[#f85149] font-mono font-semibold">SINK</span>
          </div>
        </div>

        {/* Tab bar */}
        <div className="bg-[#010409] border-b border-[#30363d] flex">
          <div className="bg-[#0d1117] border-r border-[#30363d] border-t-2 border-t-[#f0883e] px-3 py-1.5 flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#3572A5]"></div>
            <span className="text-[#e6edf3] text-[8px] font-mono">app.py</span>
          </div>
          <div className="border-r border-[#30363d] px-3 py-1.5 flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#3572A5]/50"></div>
            <span className="text-[#8b949e] text-[8px] font-mono">utils.py</span>
          </div>
          <div className="px-3 py-1.5 flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#3572A5]/50"></div>
            <span className="text-[#8b949e] text-[8px] font-mono">db.py</span>
          </div>
        </div>

        {/* Code body with gutter */}
        <div className="flex flex-1">
          {/* Gutter */}
          <div className="bg-[#161b22] border-r border-[#30363d] py-2 px-2 font-mono text-[9px] text-[#484f58] text-right leading-[20px] select-none">
            <div>4</div><div>5</div><div>6</div><div>7</div><div>8</div>
          </div>

          {/* Code */}
          <div className="py-2 px-3 font-mono text-[9px] leading-[20px] flex-1">
            <div className="text-[#8b949e]"><span className="text-[#ff7b72]">@app.route</span>(<span className="text-[#a5d6ff]">&apos;/search&apos;</span>)</div>
            <div><span className="text-[#ff7b72]">def </span><span className="text-[#d2a8ff]">search</span><span className="text-[#8b949e]">():</span></div>
            <div className="bg-[#f0883e]/[0.08] border-l-2 border-[#f0883e] -ml-3 pl-[10px] rounded-r">
              <span className="text-[#e6edf3]">    q = </span><span className="text-[#79c0ff]">request.args.get</span>(<span className="text-[#a5d6ff]">&apos;q&apos;</span>)
            </div>
            <div><span className="text-[#e6edf3]">    result = </span><span className="text-[#79c0ff]">query</span>(<span className="text-[#ffa657]">q</span>)</div>
            <div><span className="text-[#e6edf3]">    </span><span className="text-[#ff7b72]">return </span><span className="text-[#79c0ff]">jsonify</span>(result)</div>
          </div>
        </div>

        {/* Bottom: GitHub review comment */}
        <div className="absolute bottom-2.5 left-3 right-3" style={{ animation: 'ghSlide 4s ease-in-out infinite' }}>
          <div className="bg-[#1c1208] border border-[#f0883e]/30 rounded-md overflow-hidden">
            <div className="border-l-[3px] border-[#f85149] px-2.5 py-1.5 flex items-center gap-2">
              {/* Bot avatar */}
              <div className="w-4 h-4 rounded-full bg-[#21262d] border border-[#30363d] flex items-center justify-center flex-shrink-0">
                <span className="text-[#58a6ff] text-[6px] font-bold">C</span>
              </div>
              <span className="text-[#e6edf3] text-[8px] font-semibold">code-pathfinder</span>
              <span className="text-[#8b949e] text-[7px]">bot</span>
              <span className="text-[7px] px-1 py-0.5 rounded bg-[#f85149]/15 text-[#f85149] font-mono font-semibold">CRITICAL</span>
              <span className="text-[#e6edf3] text-[8px] ml-1">SQL Injection: </span>
              <span className="text-[#f0883e] text-[7px] font-mono">request.args</span>
              <span className="text-[#8b949e] text-[7px]"> → </span>
              <span className="text-[#f85149] text-[7px] font-mono">cursor.execute()</span>
              <span className="ml-auto text-[#484f58] text-[7px] font-mono">3 files</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // GitHub PR comments visualization
  if (isGitHubPR) {
    return (
      <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-slate-900 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden p-5 relative">
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(163,230,53,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(163,230,53,0.4) 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}></div>

        <div className="relative w-full h-full flex flex-col gap-2 font-mono text-[9px]">
          {/* PR header bar */}
          <div className="flex items-center gap-2 bg-gray-800/60 rounded px-2 py-1.5 border border-gray-700/50">
            <div className="w-2 h-2 rounded-full bg-green-400"></div>
            <span className="text-gray-400">Open</span>
            <span className="text-gray-300 font-semibold ml-1">fix: handle deserialization in user input</span>
            <span className="text-gray-600 ml-auto">#42</span>
          </div>

          {/* Code diff lines */}
          <div className="bg-gray-900/80 rounded border border-gray-700/40 overflow-hidden flex-shrink-0">
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-800/40 border-b border-gray-700/30">
              <span className="text-gray-500">src/api/views.py</span>
            </div>
            <div className="px-2 py-1 space-y-0.5">
              <div className="flex gap-2">
                <span className="text-gray-600 w-4 text-right">21</span>
                <span className="text-gray-500">  data = request.body</span>
              </div>
              <div className="flex gap-2 bg-red-500/10 rounded">
                <span className="text-red-500 w-4 text-right">-</span>
                <span className="text-red-400">  obj = pickle.loads(data)</span>
              </div>
              <div className="flex gap-2 bg-green-500/10 rounded">
                <span className="text-green-500 w-4 text-right">+</span>
                <span className="text-green-400">  obj = json.loads(data)</span>
              </div>
            </div>
          </div>

          {/* Inline security comment */}
          <div className="bg-amber-950/40 border border-amber-500/30 rounded px-2 py-1.5 flex gap-2 items-start">
            <div className="w-3 h-3 rounded-full bg-amber-400/80 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[6px] text-gray-900 font-bold">!</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-amber-400 font-bold">code-pathfinder</span>
                <span className="text-gray-600">•</span>
                <span className="px-1 bg-red-500/20 text-red-400 rounded text-[8px]">HIGH</span>
              </div>
              <span className="text-gray-400">python/deserialization: unsafe pickle.loads() call — arbitrary code execution risk</span>
            </div>
          </div>

          {/* PR summary badge */}
          <div className="flex items-center gap-2 mt-auto">
            <div className="flex items-center gap-1.5 bg-gray-800/60 border border-gray-700/40 rounded px-2 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
              <span className="text-gray-400">1 high</span>
            </div>
            <div className="flex items-center gap-1.5 bg-gray-800/60 border border-gray-700/40 rounded px-2 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-lime-400"></div>
              <span className="text-gray-400">3 passed</span>
            </div>
            <div className="ml-auto flex items-center gap-1 text-lime-400">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
              <span className="text-[9px]">code-pathfinder</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MCP Server - Stop Grepping, Start Querying visualization
  if (isMCP) {
    return (
      <div className="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden relative">
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(163, 230, 53, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(163, 230, 53, 0.3) 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>

        {/* Main content */}
        <div className="relative w-full h-full p-8 flex items-center justify-between">
          {/* LEFT: Grep - Slow & Messy */}
          <div className="flex-1 flex flex-col items-center relative">
            {/* Spinning loader icon */}
            <div className="relative mb-4">
              <div className="w-16 h-16 rounded-full border-4 border-red-400/20 border-t-red-400 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl">🔍</span>
              </div>
            </div>

            <div className="text-center space-y-2">
              <div className="text-red-400 text-sm font-bold">grep -r</div>
              <div className="text-gray-500 text-xs">Reading 847 files...</div>
              <div className="text-gray-600 text-[10px]">⏱️ ~30 seconds</div>

              {/* File stack visual */}
              <div className="flex items-center justify-center gap-1 mt-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-6 h-8 bg-gray-700/50 rounded border border-gray-600/50" style={{
                    transform: `rotate(${(i - 2) * 5}deg) translateY(${Math.abs(i - 2) * 2}px)`
                  }}></div>
                ))}
              </div>
            </div>
          </div>

          {/* CENTER: VS with lightning bolt */}
          <div className="flex flex-col items-center mx-8">
            <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-lime-400">
              VS
            </div>
            <div className="text-2xl mt-2">⚡</div>
          </div>

          {/* RIGHT: MCP - Fast & Precise */}
          <div className="flex-1 flex flex-col items-center relative">
            {/* Call graph visualization */}
            <div className="relative mb-4 w-20 h-20">
              {/* Center node */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-lime-400/30 rounded-full border-2 border-lime-400 flex items-center justify-center z-10">
                <div className="w-2 h-2 bg-lime-400 rounded-full animate-pulse"></div>
              </div>

              {/* Connected nodes */}
              {[0, 120, 240].map((angle, i) => (
                <div key={i}>
                  {/* Connection line */}
                  <div
                    className="absolute top-1/2 left-1/2 w-8 h-0.5 bg-lime-400/40 origin-left"
                    style={{
                      transform: `rotate(${angle}deg)`,
                      transformOrigin: '0 0'
                    }}
                  ></div>
                  {/* Outer node */}
                  <div
                    className="absolute w-3 h-3 bg-blue-400/50 rounded-full border border-blue-400"
                    style={{
                      top: `${50 + 35 * Math.sin((angle * Math.PI) / 180)}%`,
                      left: `${50 + 35 * Math.cos((angle * Math.PI) / 180)}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  ></div>
                </div>
              ))}
            </div>

            <div className="text-center space-y-2">
              <div className="text-lime-400 text-sm font-bold">MCP Query</div>
              <div className="text-gray-300 text-xs">Indexed call graph</div>
              <div className="text-lime-400 text-[10px]">✨ 87ms</div>

              {/* Success checkmarks */}
              <div className="flex items-center justify-center gap-1 mt-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="text-lime-400 text-lg">✓</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom label */}
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
          <div className="px-3 py-1 bg-lime-400/20 backdrop-blur-sm rounded-full border border-lime-400/30">
            <span className="text-lime-400 text-[9px] font-medium">Stop Grepping, Start Querying</span>
          </div>
        </div>
      </div>
    );
  }

  // OpenRouter integration visualization
  if (isOpenRouter) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden p-6">
        <div className="w-full h-full relative font-mono text-[10px]">
          {/* Window chrome */}
          <div className="absolute top-0 left-0 right-0 flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/60"></div>
            <span className="ml-2 text-gray-500 text-[9px]">SecureFlow Settings</span>
          </div>

          {/* Settings content */}
          <div className="pt-6 space-y-2 leading-relaxed">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Provider:</span>
              <span className="text-lime-400 font-bold">OpenRouter</span>
              <span className="text-gray-600">✓</span>
            </div>
            <div className="h-0.5"></div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">API Key:</span>
              <span className="text-gray-400">sk-or-v1-••••••••••••</span>
              <span className="text-green-400 text-[8px]">CONNECTED</span>
            </div>
            <div className="h-2"></div>
            <div className="text-gray-500 text-[9px]">Available Models (200+):</div>
            <div className="space-y-1 pl-2">
              <div className="flex items-center gap-2 text-gray-400">
                <span className="text-blue-400">◆</span>
                <span>anthropic/claude-3-5-sonnet</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <span className="text-red-400">◆</span>
                <span>openai/gpt-4o</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <span className="text-yellow-400">◆</span>
                <span>google/gemini-2.0-flash-exp</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <span className="text-red-400">◆</span>
                <span>deepseek/deepseek-chat</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <span className="text-green-400">◆</span>
                <span>meta-llama/llama-3.3-70b</span>
              </div>
              <div className="text-gray-600 text-[9px] pl-4">+ 195 more...</div>
            </div>
            <div className="h-1"></div>
            <div className="flex items-center gap-2">
              <span className="text-lime-400">✓</span>
              <span className="text-gray-400 text-[9px]">One API key, unlimited models</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Docker/Container scan visualization
  if (isDocker) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden p-6">
        <div className="w-full h-full relative font-mono text-[10px]">
          {/* Window chrome */}
          <div className="absolute top-0 left-0 right-0 flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/60"></div>
            <span className="ml-2 text-gray-500 text-[9px]">Terminal</span>
          </div>

          {/* Terminal content */}
          <div className="pt-6 space-y-1.5 leading-relaxed">
            <div>
              <span className="text-green-400">$</span>
              <span className="text-gray-300"> pathfinder scan --ruleset cpf/docker</span>
            </div>
            <div className="text-gray-500">Scanning Dockerfile...</div>
            <div className="h-1"></div>
            <div className="text-blue-400">Using 37 Docker security rules</div>
            <div className="h-1"></div>
            <div className="flex items-center gap-2">
              <span className="text-red-400">✗</span>
              <span className="text-gray-300">Container running as root</span>
            </div>
            <div className="text-gray-500 pl-4">→ Dockerfile:15 (CWE-250)</div>
            <div className="h-1"></div>
            <div className="flex items-center gap-2">
              <span className="text-red-400">✗</span>
              <span className="text-gray-300">Docker socket exposed in volume</span>
            </div>
            <div className="text-gray-500 pl-4">→ docker-compose.yml:8</div>
            <div className="h-1"></div>
            <div className="flex items-center gap-2">
              <span className="text-yellow-400">!</span>
              <span className="text-gray-300">Using latest tag without digest</span>
            </div>
            <div className="text-gray-500 pl-4">→ Dockerfile:1</div>
            <div className="h-2"></div>
            <div className="flex gap-3 text-[9px]">
              <span className="text-red-400">2 critical</span>
              <span className="text-yellow-400">1 medium</span>
              <span className="text-gray-500">Scanned in 0.8s</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // CLI-style terminal mockup
  if (isCLI) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden p-6">
        <div className="w-full h-full relative font-mono text-[10px]">
          {/* Window chrome */}
          <div className="absolute top-0 left-0 right-0 flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/60"></div>
            <span className="ml-2 text-gray-500 text-[9px]">Terminal</span>
          </div>

          {/* Terminal content */}
          <div className="pt-6 space-y-1.5 leading-relaxed">
            <div>
              <span className="text-green-400">$</span>
              <span className="text-gray-300"> secureflow scan --project ./app</span>
            </div>
            <div className="text-gray-500">Scanning 847 files...</div>
            <div className="h-1"></div>
            <div className="text-blue-400">Using model: claude-sonnet-4-20250514</div>
            <div className="h-1"></div>
            <div className="flex items-center gap-2">
              <span className="text-yellow-400">!</span>
              <span className="text-gray-300">Found potential SQL injection</span>
            </div>
            <div className="text-gray-500 pl-4">→ src/db/queries.py:42</div>
            <div className="h-1"></div>
            <div className="flex items-center gap-2">
              <span className="text-green-400">✓</span>
              <span className="text-gray-300">Scan complete: 3 issues found</span>
            </div>
            <div className="h-2"></div>
            <div className="flex gap-3 text-[9px]">
              <span className="text-red-400">0 critical</span>
              <span className="text-yellow-400">1 high</span>
              <span className="text-blue-400">2 medium</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isExtension || isProduct) {
    // Product/Extension - Dashboard style
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden p-6">
        <div className="w-full h-full relative">
          {/* Window chrome */}
          <div className="absolute top-0 left-0 right-0 flex items-center gap-1.5 mb-3">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-400/60"></div>
          </div>

          {/* Dashboard content */}
          <div className="pt-6 grid grid-cols-3 gap-2 h-full">
            {/* Stat cards */}
            <div className="bg-gray-800/50 rounded p-2 border border-gray-700/50">
              <div className="w-8 h-1.5 bg-gray-600 rounded mb-1"></div>
              <div className="text-lime-400 text-lg font-bold">0</div>
              <div className="w-6 h-1 bg-green-500/40 rounded mt-1"></div>
            </div>
            <div className="bg-gray-800/50 rounded p-2 border border-gray-700/50">
              <div className="w-8 h-1.5 bg-gray-600 rounded mb-1"></div>
              <div className="text-yellow-400 text-lg font-bold">3</div>
              <div className="w-6 h-1 bg-yellow-500/40 rounded mt-1"></div>
            </div>
            <div className="bg-gray-800/50 rounded p-2 border border-gray-700/50">
              <div className="w-8 h-1.5 bg-gray-600 rounded mb-1"></div>
              <div className="text-blue-400 text-lg font-bold">12</div>
              <div className="w-6 h-1 bg-blue-500/40 rounded mt-1"></div>
            </div>

            {/* Chart area */}
            <div className="col-span-3 bg-gray-800/30 rounded p-2 border border-gray-700/30 flex items-end justify-around gap-1">
              {[40, 65, 45, 80, 55, 70, 35, 60, 75, 50].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 bg-gradient-to-t from-lime-500/60 to-lime-400/40 rounded-sm"
                  style={{ height: `${h}%` }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Engineering - Code/Terminal style
  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg aspect-video mb-4 flex items-center justify-center overflow-hidden p-6">
      <div className="w-full h-full relative font-mono text-xs">
        {/* Window chrome */}
        <div className="absolute top-0 left-0 right-0 flex items-center gap-1.5 mb-3">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/60"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-green-400/60"></div>
        </div>

        {/* Code content */}
        <div className="pt-6 space-y-1.5 text-[10px] leading-relaxed">
          <div>
            <span className="text-red-400">from</span>
            <span className="text-gray-300"> codepathfinder </span>
            <span className="text-red-400">import</span>
            <span className="text-yellow-300"> rule</span>
          </div>
          <div className="h-1.5"></div>
          <div>
            <span className="text-yellow-300">@rule</span>
            <span className="text-gray-400">(</span>
            <span className="text-orange-300">id</span>
            <span className="text-gray-400">=</span>
            <span className="text-green-400">&quot;sql-injection&quot;</span>
            <span className="text-gray-400">)</span>
          </div>
          <div>
            <span className="text-red-400">def</span>
            <span className="text-blue-300"> detect</span>
            <span className="text-gray-400">():</span>
          </div>
          <div className="pl-4">
            <span className="text-red-400">return</span>
            <span className="text-blue-300"> Call</span>
            <span className="text-gray-400">(</span>
            <span className="text-green-400">&quot;execute&quot;</span>
            <span className="text-gray-400">)</span>
          </div>

          {/* Highlight bar */}
          <div className="mt-3 flex items-center gap-2">
            <div className="h-1 flex-1 bg-lime-500/30 rounded"></div>
            <span className="text-lime-400 text-[8px]">Found 0 issues</span>
          </div>
        </div>
      </div>
    </div>
  );
}
